import { } from '@tabler/icons-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import classes from './LegalPage.module.css';
import { Logo } from '../components/Logo.js';

/**
 * Privacy and support.
 *
 * Both are required fields on the CDK Marketplace listing, and a reviewer
 * clicks them. They are also the pages a dealership reads before letting a
 * third party near their customers' records, so the content describes what
 * this platform actually stores and why, taken from the code rather than from
 * a template.
 *
 * Deliberately plain routes with no authentication: a privacy policy behind a
 * login is not a privacy policy.
 */

const SUPPORT_EMAIL = 'support@liveaios.io';
const UPDATED = '3 September 2026';

function Shell({ title, updated, lede, children }: {
  title: string;
  updated?: string;
  lede: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className={classes.page}>
      <header className={classes.header}>
        <div className={classes.headerInner}>
          <Link to="/" className={classes.wordmark}>
            <Logo on="light" height={26} />
          </Link>
          <Link to="/" className={classes.back}>
            Back to site
          </Link>
        </div>
      </header>

      <main className={classes.shell}>
        <h1 className={classes.h1}>{title}</h1>
        {updated && <p className={classes.updated}>Last updated {updated}</p>}
        <p className={classes.lede}>{lede}</p>
        <div className={classes.prose}>{children}</div>
        <p className={classes.footer}>
          Questions about this page? <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
        </p>
      </main>
    </div>
  );
}

export function PrivacyPage() {
  return (
    <Shell
      title="Privacy"
      updated={UPDATED}
      lede={
        <>
          Live AI OS is an integration between a dealership’s CRM and the tools that dealership
          has chosen to work its enquiries. Almost all of the personal data we touch belongs to
          the dealership and its customers, and we handle it on the dealership’s instructions.
        </>
      }
    >
      <h2>Who is responsible for the data</h2>
      <p>
        <strong>The dealership is the controller.</strong> They decide what happens to their
        customers’ information. Live AI OS is a processor: we move data between the dealership’s
        CRM and the systems they have asked us to connect, and we do not decide what it is used
        for.
      </p>
      <p>
        Live AI OS is operated by Next Level Agency. If you are a customer of a dealership and
        want your data corrected or removed, contact the dealership — they hold the record, and
        we act on their instruction.
      </p>

      <h2>What we hold, and why</h2>
      <table className={classes.table}>
        <tbody>
          <tr>
            <th>Enquiry details</th>
            <td>
              Name, email address, phone number, the vehicle enquired about, and where the
              enquiry came from. Read from the dealership’s CRM so their agent can respond, and
              written back when an appointment is booked.
            </td>
          </tr>
          <tr>
            <th>Appointment and call records</th>
            <td>
              When a call happened, how long it lasted, the number dialled, and the appointment
              booked. Written into the dealership’s CRM so their staff see it where they already
              work.
            </td>
          </tr>
          <tr>
            <th>Request logs</th>
            <td>
              Every call we make to the CRM, with its request id, status and timing. Kept so a
              failure can be traced and so the dealership can see what we did on their behalf.
            </td>
          </tr>
          <tr>
            <th>Console accounts</th>
            <td>
              The email address, role and sign-in times of people given access to the console or
              the dealership portal. Kept so access can be granted, audited and withdrawn.
            </td>
          </tr>
        </tbody>
      </table>

      <div className={classes.note}>
        <p>
          <strong>What we do not do.</strong> We do not sell personal data, we do not share it
          between dealerships, and we do not use one dealership’s customer records to train
          anything or to benefit another. A dealership’s credentials cannot reach another
          dealership’s data.
        </p>
      </div>

      <h2>Who else sees it</h2>
      <p>
        Only the services required to do the work the dealership asked for. In practice that
        means the dealership’s own CRM, the voice or messaging platform they have chosen for
        their agent, and the email provider configured to send from their address. Each receives
        only what that specific task needs.
      </p>
      <p>
        We also use ordinary infrastructure providers for hosting and databases. They store data
        on our behalf and are not permitted to use it for anything else.
      </p>

      <h2>How long we keep it</h2>
      <p>
        Enquiry records are held while they are being worked and are removed once the dealership
        stops using the service. Request logs are kept as an operational and audit record.
        Account records are removed when the account is removed.
      </p>
      <p>
        A dealership ending its subscription can ask us to delete everything we hold for that
        store, and we will.
      </p>

      <h2>Keeping it safe</h2>
      <p>
        Traffic is encrypted in transit. Credentials are encrypted at rest and are never written
        to logs. Access to the console is by invitation only, is scoped so that a person or an
        automation can only reach the dealerships they have been given, and every privileged
        action is recorded against the person who took it.
      </p>

      <h2>Changes</h2>
      <p>
        If this page changes in a way that affects what we do with personal data, we will tell
        the dealerships using the service rather than relying on them to notice.
      </p>

      <h2>Contact</h2>
      <p>
        Write to <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>. If you are a
        dealership customer, your dealership is the faster route — they hold the record.
      </p>
    </Shell>
  );
}

export function SupportPage() {
  return (
    <Shell
      title="Support"
      lede={
        <>
          Live AI OS connects AI agents to a dealership’s CRM. If something is not arriving, not
          booking, or not sending, this is where to start.
        </>
      }
    >
      <h2>Getting help</h2>
      <p>
        Email <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>. Include the dealership
        name and, if you have it, the request id shown against the failure in the console — that
        is the fastest way for us to find the exact call in the logs.
      </p>

      <h2>Before you write</h2>
      <ul>
        <li>
          <strong>Enquiries are not arriving.</strong> Open the dealership in the console and
          press <strong>Poll now</strong>. It runs the same check the schedule runs and reports
          what the CRM actually said.
        </li>
        <li>
          <strong>Emails are not sending.</strong> Every email path needs a sending address that
          the CRM already recognises as a sender for that store. Check the dealership’s
          configuration first.
        </li>
        <li>
          <strong>Appointments are refused.</strong> The CRM validates the lead source and up
          type as a pair. A combination it does not recognise is rejected, so pick one the
          dealership actually uses.
        </li>
      </ul>

      <h2>For developers</h2>
      <p>
        The API reference lives in the console under <strong>API docs</strong>, with a playground
        for sending real requests against your own keys. Every endpoint is documented with its
        required scope, its fields and a runnable example.
      </p>

      <h2>Reporting something urgent</h2>
      <p>
        If customer data appears to be wrong, missing, or visible to the wrong dealership, say so
        in the subject line and we will treat it ahead of everything else.
      </p>
    </Shell>
  );
}
